import React, { useState } from "react";
import api from "../utils/api";
import "./LeaveReview.css";

const STAR_LABELS = ["", "Poor", "Fair", "Good", "Great", "Excellent"];

function StarPicker({ value, onChange }) {
  const [hovered, setHovered] = useState(0);
  const active = hovered || value;
  return (
    <div className="lr-stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          className={`lr-star${n <= active ? " lr-star--on" : ""}`}
          onMouseEnter={() => setHovered(n)}
          onMouseLeave={() => setHovered(0)}
          onClick={() => onChange(n)}
          aria-label={`${n} star${n > 1 ? "s" : ""}`}
        >
          ★
        </button>
      ))}
      {active > 0 && (
        <span className="lr-star-label">{STAR_LABELS[active]}</span>
      )}
    </div>
  );
}

function LeaveReview() {
  const [form, setForm] = useState({
    reviewer_name: "",
    reviewer_area: "",
    instructor_name: "",
    rating: 0,
    title: "",
    body: "",
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [serverError, setServerError] = useState("");

  const h = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.reviewer_name.trim()) e.reviewer_name = "Please enter your name";
    if (!form.rating) e.rating = "Please select a star rating";
    if (!form.body.trim())
      e.body = "Please write something about your experience";
    else if (form.body.trim().length < 20)
      e.body = "Review must be at least 20 characters";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus("submitting");
    try {
      await api.post("/reviews", form);
      setStatus("success");
    } catch (err) {
      setServerError(err.message || "Something went wrong. Please try again.");
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div className="lr-page">
        <div className="lr-hero">
          <div className="lr-hero-inner">
            <h1 className="lr-hero-title">Leave a Review</h1>
          </div>
        </div>
        <div className="lr-card-wrap">
          <div className="lr-card lr-card--success">
            <div className="lr-success-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="52"
                height="52"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="9 12 11 14 15 10" />
              </svg>
            </div>
            <h2>Thank You!</h2>
            <p>
              Your review has been submitted and is awaiting approval. We
              appreciate you taking the time to share your experience with Fast
              Track Driving School.
            </p>
            <a href="/" className="lr-btn lr-btn--primary">
              Back to Home
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="lr-page">
      {/* Hero */}
      <div className="lr-hero">
        <div className="lr-hero-inner">
          <span className="lr-eyebrow">Fast Track Driving School</span>
          <h1 className="lr-hero-title">Leave a Review</h1>
          <p className="lr-hero-sub">
            Share your experience so other learners can find a great instructor
          </p>
        </div>
      </div>

      <div className="lr-card-wrap">
        <div className="lr-card">
          {status === "error" && (
            <div className="lr-server-error">{serverError}</div>
          )}

          <form className="lr-form" onSubmit={handleSubmit} noValidate>
            <div className="lr-row">
              <div
                className={`lr-field${errors.reviewer_name ? " lr-field--err" : ""}`}
              >
                <label htmlFor="lr-name">Your Name *</label>
                <input
                  id="lr-name"
                  type="text"
                  autoComplete="name"
                  placeholder="e.g. Sarah M."
                  value={form.reviewer_name}
                  onChange={h("reviewer_name")}
                />
                {errors.reviewer_name && (
                  <span className="lr-err">{errors.reviewer_name}</span>
                )}
              </div>

              <div className="lr-field">
                <label htmlFor="lr-area">Your Area (optional)</label>
                <input
                  id="lr-area"
                  type="text"
                  placeholder="e.g. East London"
                  value={form.reviewer_area}
                  onChange={h("reviewer_area")}
                />
              </div>
            </div>

            <div className="lr-field">
              <label htmlFor="lr-instructor">Instructor Name (optional)</label>
              <input
                id="lr-instructor"
                type="text"
                placeholder="Name of your instructor if you'd like to mention them"
                value={form.instructor_name}
                onChange={h("instructor_name")}
              />
            </div>

            <div className={`lr-field${errors.rating ? " lr-field--err" : ""}`}>
              <label>Overall Rating *</label>
              <StarPicker
                value={form.rating}
                onChange={(v) => {
                  setForm((f) => ({ ...f, rating: v }));
                  setErrors((e) => {
                    const n = { ...e };
                    delete n.rating;
                    return n;
                  });
                }}
              />
              {errors.rating && <span className="lr-err">{errors.rating}</span>}
            </div>

            <div className="lr-field">
              <label htmlFor="lr-title">Review Title (optional)</label>
              <input
                id="lr-title"
                type="text"
                maxLength={120}
                placeholder="e.g. Passed first time — brilliant!"
                value={form.title}
                onChange={h("title")}
              />
            </div>

            <div className={`lr-field${errors.body ? " lr-field--err" : ""}`}>
              <label htmlFor="lr-body">Your Review *</label>
              <textarea
                id="lr-body"
                rows={5}
                maxLength={1500}
                placeholder="Tell us about your experience — your instructor, the booking process, passing your test…"
                value={form.body}
                onChange={h("body")}
              />
              <span className="lr-char-count">{form.body.length}/1500</span>
              {errors.body && <span className="lr-err">{errors.body}</span>}
            </div>

            <p className="lr-disclaimer">
              Reviews are moderated before going live. We'll display your first
              name and area only — no personal contact details are published.
            </p>

            <button
              type="submit"
              className="lr-btn lr-btn--primary"
              disabled={status === "submitting"}
            >
              {status === "submitting" ? (
                <>
                  <span className="lr-spinner" /> Submitting…
                </>
              ) : (
                "Submit Review →"
              )}
            </button>

            <div className="lr-google-cta">
              <p>Or leave us a review directly on Google:</p>
              <a
                href="https://g.page/r/REPLACE_WITH_YOUR_GOOGLE_PLACE_ID/review"
                target="_blank"
                rel="noopener noreferrer"
                className="lr-btn lr-btn--google"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
                Review on Google
              </a>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default LeaveReview;
